// ตำแหน่งของเราระหว่างชีวิต (docs/PLAN.md ข้อ 4.3)
// แยกจาก engine: หลังเลือกตัวเลือกแต่ละครั้ง หน้าเกมเรียก advanceTrack ให้ย้ายตำแหน่งตามความสามารถในการเดินทาง
// สุ่มด้วย seed ของชีวิต + turn จึงเล่นซ้ำด้วย seed เดิมได้ตำแหน่งเดิม

import { inBoxes, migrantOf, type Box } from "../data/migrants";
import { GRID_COLS, cellBounds, cellOf, haversineKm, neighbors, type LatLon } from "../engine/grid";
import { currentStage, stagesOf } from "../engine/life";
import { Rng, hashSeed } from "../engine/rng";
import type { LifeState, SpeciesInfo, StageDef } from "../engine/types";
import { SHELF_DEPTH, type BiomeId } from "./biomes";
import { FULL_LAND, hasLand, hasMangrove, hasSea, landBiomeAt, type GeoGrid } from "./geodata";
import { flywayFor, flywayNames, groundKind, groundName, migrationAtBirth } from "./migration";
import { placeName, type Altitude } from "./place";
import { realmOf, type Realm, type SpeciesRange } from "./range";

/** ตำแหน่งในแนวตั้งและประเภทพื้นที่ตรงที่เราอยู่ */
export interface Position extends Altitude {
  biome: BiomeId | null;
  mangrove: boolean; // ช่องนี้มีป่าชายเลน (ชายฝั่ง) เหตุการณ์ป่าชายเลนเกิดได้ทั้งสัตว์บกและสัตว์น้ำ
}

export interface GeoTrack extends Position {
  cell: number;
  at: LatLon;
  path: LatLon[]; // ทุกจุดที่เคยอยู่ เรียงตามเวลา เริ่มจากจุดเกิด
  km: number; // ระยะทางรวมโดยประมาณ
  stageId: string; // ช่วงวัยตอนคำนวณความสูงครั้งล่าสุด
  highest: number;
  lowest: number;
  birth: { cell: number; onSea: boolean }; // จุดเกิด ใช้บอกในหน้าสรุป
}

const LEVEL_WEIGHT = [0, 1, 2, 4]; // ถ่วงตามระดับความหนาแน่นของการพบ 1–3

// ตัวอ่อนที่ลอยไปกับกระแสน้ำ
const DRIFTING_STAGES = new Set(["larva", "paralarva", "planula", "ephyra"]);
// สัตว์ที่เดินทางไกลในทะเล
const FAR_SWIMMERS = new Set(["mammal.whale", "fish.shark_ray", "reptile.turtle"]);
// สัตว์ตัวเล็กที่ทั้งชีวิตไปไม่ถึงช่องข้างๆ (ช่องละราว 100 กม.) เช่น มด แมงมุม กบ หนู กิ้งก่า จึงอยู่ช่องเดิมตลอด
// ยกเว้นตัวอ่อนในทะเลที่ลอยไปกับกระแสน้ำ และแมลงที่บินได้ไกลจริง (ผีเสื้อ แมลงปอ ตั๊กแตนที่รวมฝูงบิน) เมื่อโตจนบินได้แล้ว
const HOMEBODY_ARCHETYPES: ReadonlySet<string> = new Set(["INSECT", "SOCIAL_INSECT", "ARACHNID", "MYRIAPOD", "WORM", "AMPHIBIAN", "CRUSTACEAN", "MOLLUSC", "ECHINODERM"]);
const HOMEBODY_SUBGROUPS: ReadonlySet<string> = new Set(["mammal.rodent", "reptile.lizard", "reptile.snake"]);
const STRONG_FLIERS: ReadonlySet<string> = new Set(["insect.butterfly", "insect.dragonfly", "insect.grasshopper"]);

function staysHome(species: SpeciesInfo, stage: StageDef): boolean {
  if (species.subgroupId === "mollusc.cephalopod") return false;
  if (STRONG_FLIERS.has(species.subgroupId) && species.traits.canFly && (stage.tag === "adult" || stage.tag === "old")) return false;
  return HOMEBODY_ARCHETYPES.has(species.archetype) || HOMEBODY_SUBGROUPS.has(species.subgroupId);
}

type Lifestyle = "ground" | "pelagic" | "benthic" | "plankton";

function lifestyle(species: SpeciesInfo, stage: StageDef, realm: Realm): Lifestyle {
  if (realm !== "land" && DRIFTING_STAGES.has(stage.id)) return "plankton";
  const sg = species.subgroupId;
  switch (species.archetype) {
    case "FISH":
      return "pelagic";
    case "MAMMAL":
      return sg === "mammal.whale" ? "pelagic" : "ground";
    case "REPTILE":
      return sg === "reptile.turtle" ? "pelagic" : "ground";
    case "MOLLUSC":
      return sg === "mollusc.cephalopod" ? "pelagic" : "benthic";
    case "SESSILE":
      return sg === "cnidarian.jellyfish" && stage.id !== "polyp" ? "pelagic" : "benthic";
    case "CRUSTACEAN":
      return sg === "crustacean.decapod" ? "benthic" : "pelagic";
    case "ECHINODERM":
    case "WORM":
      return "benthic";
    default:
      // นก แมลง และสัตว์บก ถ้าอยู่กลางทะเลก็อยู่ที่ผิวน้ำ
      return "ground";
  }
}

/** อยู่ในน้ำทะเลหรือบนบก ไข่และลูกอ่อนของสัตว์ที่ขึ้นมาวางไข่บนบก (เต่า นก) อยู่บนบก */
function onSeaAt(grid: GeoGrid, cell: number, species: SpeciesInfo, stage: StageDef, realm: Realm): boolean {
  if (realm === "land") return !hasLand(grid, cell);
  if (realm === "sea") return true;
  const nestsOnLand = ["REPTILE", "BIRD", "MAMMAL"].includes(species.archetype) && (stage.tag === "dormant" || stage.tag === "dependent");
  if (nestsOnLand && hasLand(grid, cell)) return false;
  return hasSea(grid, cell);
}

function between(rng: Rng, min: number, max: number): number {
  return Math.round(min + rng.next() * Math.max(0, max - min));
}

export function positionAt(grid: GeoGrid, cell: number, species: SpeciesInfo, stage: StageDef, rng: Rng): Position {
  const realm = realmOf(species.traits);
  const onSea = onSeaAt(grid, cell, species, stage, realm);
  const mangrove = hasMangrove(grid, cell) && hasLand(grid, cell) && hasSea(grid, cell);
  if (!onSea) return { onSea, altitude: grid.elevation[cell], ground: grid.elevation[cell], biome: landBiomeAt(grid, cell), mangrove };
  const depth = Math.max(grid.depth[cell], 5);
  let d = 0;
  switch (lifestyle(species, stage, realm)) {
    case "plankton":
      d = between(rng, 1, Math.min(30, depth));
      break;
    case "pelagic":
      d = between(rng, 2, Math.min(200, depth));
      break;
    case "benthic":
      // สัตว์หน้าดินส่วนใหญ่อยู่บริเวณที่ตื้นของช่อง (ไหล่ทวีป แนวปะการัง)
      d = Math.min(depth, between(rng, 2, species.subgroupId === "cnidarian.coral" ? 40 : 60));
      break;
    case "ground":
      d = 0;
  }
  return { onSea, altitude: -d, ground: -depth, biome: depth < SHELF_DEPTH ? "coastal_sea" : "open_ocean", mangrove };
}

function randomPoint(cell: number, rng: Rng): LatLon {
  const b = cellBounds(cell);
  const r = (x: number) => Math.round(x * 100) / 100;
  return { lat: r(b.south + 0.1 + rng.next() * 0.8), lon: r(b.west + 0.1 + rng.next() * 0.8) };
}

function pickWeighted(cells: readonly { cell: number; level: number }[], rng: Rng): number {
  return cells[rng.weightedIndex(cells.map((c) => LEVEL_WEIGHT[c.level] ?? 1))].cell;
}

const rangeIndex = new WeakMap<SpeciesRange, Map<number, number>>();
function levels(range: SpeciesRange): Map<number, number> {
  let m = rangeIndex.get(range);
  if (!m) {
    m = new Map(range.cells.map((c) => [c.cell, c.level]));
    rangeIndex.set(range, m);
  }
  return m;
}

function trackAt(cell: number, at: LatLon, alt: Position, stageId: string, prev?: GeoTrack): GeoTrack {
  const path = prev ? [...prev.path, at] : [at];
  const km = prev ? prev.km + haversineKm(prev.at, at) : 0;
  return {
    cell,
    at,
    path,
    km,
    stageId,
    ...alt,
    highest: Math.max(prev?.highest ?? -Infinity, alt.altitude),
    lowest: Math.min(prev?.lowest ?? Infinity, alt.altitude),
    birth: prev?.birth ?? { cell, onSea: alt.onSea },
  };
}

const cellInBoxes = (cell: number, boxes: readonly Box[]) => {
  const b = cellBounds(cell);
  return inBoxes(boxes, b.south + 0.5, b.west + 0.5);
};

/**
 * ช่องในกรอบของแหล่งอพยพ ใช้ช่องในถิ่นที่อยู่ก่อน ถ้าถิ่นที่อยู่ (เช่น ที่ประมาณจากรายชื่อประเทศ) ไม่ครอบคลุมแหล่งนั้น
 * ใช้ทุกช่องในกรอบที่ตัวกลางตรงกับชนิด (บก/ทะเล)
 */
function groundCells(boxes: readonly Box[], range: SpeciesRange, grid: GeoGrid, realm: Realm): { cell: number; level: number }[] {
  const fromRange = range.cells.filter((c) => cellInBoxes(c.cell, boxes));
  if (fromRange.length) return fromRange;
  const out: { cell: number; level: number }[] = [];
  for (const [s, w, n, e] of boxes) {
    for (let lat = Math.floor(s) + 0.5; lat < n; lat++) {
      for (let lon = Math.floor(w) + 0.5; lon < e; lon++) {
        const cell = cellOf(lat, lon);
        const ok = realm === "sea" ? hasSea(grid, cell) : realm === "land" ? hasLand(grid, cell) : true;
        if (ok) out.push({ cell, level: 1 });
      }
    }
  }
  return out;
}

/**
 * จุดเกิด: สุ่มช่องจากถิ่นที่อยู่ ถ่วงด้วยความหนาแน่น แล้วสุ่มจุดในช่อง สัตว์อพยพเกิดที่แหล่งผสมพันธุ์
 * ลูกที่เล่นต่อจากพ่อแม่เกิดตรงที่พ่อแม่ออกลูก (birthAt)
 */
export function startTrack(species: SpeciesInfo, seed: string, range: SpeciesRange, grid: GeoGrid, birthAt?: LatLon): GeoTrack {
  const rng = new Rng(hashSeed(`${seed}:${species.id}:geo`));
  if (birthAt) {
    const cell = cellOf(birthAt.lat, birthAt.lon);
    const stage = stagesOf(species)[0];
    return trackAt(cell, birthAt, positionAt(grid, cell, species, stage, rng), stage.id);
  }
  const migrant = migrantOf(species.sci);
  const breeding = migrant ? range.cells.filter((c) => migrant.routes.some((r) => cellInBoxes(c.cell, r.a))) : [];
  const cell = pickWeighted(breeding.length ? breeding : range.cells, rng);
  const stage = stagesOf(species)[0];
  return trackAt(cell, randomPoint(cell, rng), positionAt(grid, cell, species, stage, rng), stage.id);
}

interface Mobility {
  chance: number; // โอกาสย้ายใน turn นี้
  reach: number; // ไกลสุดกี่ช่อง
}

export function mobility(species: SpeciesInfo, stage: StageDef, move?: "near" | "far" | "migrate"): Mobility {
  if (stage.tag === "dormant") return { chance: 0, reach: 0 };
  if (species.traits.sessile && (stage.tag === "adult" || stage.tag === "old")) return { chance: 0, reach: 0 };
  const realm = realmOf(species.traits);
  let base: Mobility;
  if (realm !== "land" && DRIFTING_STAGES.has(stage.id)) base = { chance: 0.5, reach: 2 };
  // มดงานไม่มีปีกและทั้งรังอยู่ที่เดิม สัตว์ตัวเล็กอื่นก็ไปได้แค่ไม่กี่กิโลเมตร ไม่พ้นช่องเดิม
  else if (staysHome(species, stage)) return { chance: 0, reach: 0 };
  else if (stage.tag === "dependent") base = { chance: 0.1, reach: 1 }; // ไปไหนก็ไปกับพ่อแม่
  else if (species.archetype === "BIRD" && stage.tag !== "young") base = { chance: 0.35, reach: 3 };
  else if (FAR_SWIMMERS.has(species.subgroupId) && stage.tag !== "young") base = { chance: 0.35, reach: 3 };
  else if (species.traits.canFly && stage.tag !== "young") base = { chance: 0.2, reach: 1 };
  else if (["MAMMAL", "REPTILE", "FISH"].includes(species.archetype) || species.subgroupId === "mollusc.cephalopod") base = { chance: 0.15, reach: 1 };
  else base = { chance: 0.05, reach: 1 };
  if (move === "near") return { chance: 1, reach: Math.max(1, base.reach) };
  if (move === "far") return { chance: 1, reach: Math.max(4, base.reach) };
  return base;
}

/**
 * ทางไปช่องเป้าหมายต้องผ่านพื้นที่ที่ไปได้ทุกช่อง สัตว์ทะเลไม่ข้ามแผ่นดิน (เช่น คอคอดกระ) สัตว์บกไม่ข้ามทะเล
 * สัตว์ที่บินได้และสัตว์ที่อยู่ได้ทั้งบนบกและในทะเลไปได้ทุกทาง
 */
export function passable(grid: GeoGrid, from: number, to: number, species: SpeciesInfo, stage: StageDef, onSea: boolean): boolean {
  const realm = realmOf(species.traits);
  const flying = species.traits.canFly && stage.tag !== "young" && stage.tag !== "dependent";
  if (flying || realm === "both") return true;
  const r0 = Math.floor(from / GRID_COLS);
  const r1 = Math.floor(to / GRID_COLS);
  const c0 = from % GRID_COLS;
  let dc = (to % GRID_COLS) - c0;
  if (dc > GRID_COLS / 2) dc -= GRID_COLS;
  if (dc < -GRID_COLS / 2) dc += GRID_COLS;
  const steps = Math.max(Math.abs(r1 - r0), Math.abs(dc)) * 2;
  for (let i = 1; i <= steps; i++) {
    const r = Math.round(r0 + ((r1 - r0) * i) / steps);
    const c = (((Math.round(c0 + (dc * i) / steps)) % GRID_COLS) + GRID_COLS) % GRID_COLS;
    const cell = r * GRID_COLS + c;
    if (onSea ? grid.land[cell] === FULL_LAND : grid.land[cell] === 0) return false;
  }
  return true;
}

function moveText(species: SpeciesInfo, stage: StageDef, track: GeoTrack, km: number, place: string): string {
  const realm = realmOf(species.traits);
  const distance = km < 10 ? Math.max(1, Math.round(km)) : Math.round(km / 10) * 10;
  const verb =
    realm !== "land" && DRIFTING_STAGES.has(stage.id)
      ? "กระแสน้ำพาคุณลอยไปราว"
      : species.traits.canFly && stage.tag !== "young" && stage.tag !== "dependent"
        ? "คุณบินไปราว"
        : track.onSea
          ? "คุณว่ายไปราว"
          : "คุณเดินทางไปราว";
  return `${verb} ${distance.toLocaleString("th-TH")} กม. ตอนนี้อยู่แถว${place}`;
}

/** หลังจบแต่ละ turn: อาจย้ายไปช่องข้างเคียงในถิ่นที่อยู่ และคำนวณความสูงใหม่เมื่อย้ายหรือเปลี่ยนช่วงวัย */
export function advanceTrack(state: LifeState, range: SpeciesRange, grid: GeoGrid): LifeState {
  const stage = currentStage(state);
  if (!state.geo) {
    const geo = startTrack(state.species, state.seed, range, grid, state.birthAt);
    const migration = migrationAtBirth(state.species, geo.at);
    return { ...state, geo, move: undefined, ...(migration ? { migration } : {}) };
  }
  const prev = state.geo;
  const rng = new Rng(hashSeed(`${state.seed}:geo:${state.turn}`));
  if (state.move === "migrate") return migrate(state, range, grid, rng);
  const { chance, reach } = state.alive ? mobility(state.species, stage, state.move) : { chance: 0, reach: 0 };

  let target: number | null = null;
  if (reach > 0 && rng.chance(chance)) {
    const known = levels(range);
    const options = neighbors(prev.cell, reach)
      .filter((c) => known.has(c) && passable(grid, prev.cell, c, state.species, stage, prev.onSea))
      .map((cell) => ({ cell, level: known.get(cell)! }));
    if (options.length) target = pickWeighted(options, rng);
  }

  if (target === null) {
    if (prev.stageId === stage.id) return state.move ? { ...state, move: undefined } : state;
    // ช่วงวัยใหม่อาจอยู่ความลึกต่างจากเดิม เช่น ตัวอ่อนแพลงก์ตอนลงไปอยู่พื้นทะเล
    const alt = positionAt(grid, prev.cell, state.species, stage, rng);
    return {
      ...state,
      move: undefined,
      geo: { ...prev, ...alt, stageId: stage.id, highest: Math.max(prev.highest, alt.altitude), lowest: Math.min(prev.lowest, alt.altitude) },
    };
  }

  const at = randomPoint(target, rng);
  const geo = trackAt(target, at, positionAt(grid, target, state.species, stage, rng), stage.id, prev);
  const text = moveText(state.species, stage, geo, geo.km - prev.km, placeName(grid, target, geo.onSea, at));
  return {
    ...state,
    move: undefined,
    geo,
    log: [...state.log, { turn: state.turn, ageDays: state.ageDays, stageId: stage.id, text }],
  };
}

/** อพยพไปอีกแหล่งตามเส้นทางของประชากรที่เราเกิด (ผล move: "migrate") */
function migrate(state: LifeState, range: SpeciesRange, grid: GeoGrid, rng: Rng): LifeState {
  const prev = state.geo!;
  const stage = currentStage(state);
  const migrant = migrantOf(state.species.sci);
  const heading = state.migration?.heading;
  const route = migrant && state.migration ? migrant.routes[state.migration.route] : undefined;
  const migration = state.migration ? { route: state.migration.route, month0: state.migration.month0 } : undefined;
  // ปลาแซลมอนอยู่ได้ทั้งแม่น้ำและทะเล ไม่ว่าข้อมูลถิ่นที่อยู่จะบอกว่าอยู่น้ำจืดหรือทะเลอย่างเดียว
  const anadromous = migrant?.cycle === "anadromous";
  const realm = anadromous ? "both" : realmOf(state.species.traits);
  const options = route && heading ? groundCells(heading === "a" ? route.a : route.b, range, grid, realm) : [];
  if (!migrant || !route || !heading || !options.length) return { ...state, move: undefined, migration };
  // ปลาแซลมอนกลับไปวางไข่ที่แม่น้ำสายที่ตัวเองเกิด เต่าทะเลกลับไปวางไข่ที่หาดที่ตัวเองฟัก
  const home = !!migrant.cycle && heading === "a" && cellInBoxes(prev.birth.cell, route.a);
  const target = home ? prev.birth.cell : pickWeighted(options, rng);
  const at = home ? prev.path[0] : randomPoint(target, rng);
  // เดินทางผ่านจุดแวะตามเส้นทางจริง เส้นบนแผนที่และระยะทางจึงตามเส้นทางนั้น
  const flyway = flywayFor(route, heading, prev.at, at);
  let via = prev;
  for (const [lat, lon] of flyway) {
    const p = { lat, lon };
    via = { ...via, path: [...via.path, p], km: via.km + haversineKm(via.at, p), at: p };
  }
  const geo = trackAt(target, at, positionAt(grid, target, state.species, stage, rng), stage.id, via);
  const km = Math.round((geo.km - prev.km) / 10) * 10;
  const passed = flywayNames(flyway);
  const text = `คุณอพยพไกลราว ${km.toLocaleString("th-TH")} กม.${passed ? ` ผ่าน${passed}` : ""} ถึง${home ? (anadromous ? "แม่น้ำสายที่คุณเกิด" : "ชายหาดที่คุณฟักออกมา") : `${groundKind(migrant, heading)}ที่${groundName(route, heading)}`} ตอนนี้อยู่แถว${placeName(grid, target, geo.onSea, at)}`;
  return {
    ...state,
    move: undefined,
    migration,
    migrations: (state.migrations ?? 0) + 1,
    geo,
    // บันทึกที่อายุเดียวกับตอนเลือกออกเดินทาง ไม่ใช่หลังจบ turn
    log: [...state.log, { turn: state.turn, ageDays: state.log.at(-1)?.ageDays ?? state.ageDays, stageId: stage.id, text }],
  };
}
